import React, { createContext, useContext, useState } from "react";
import { Expense } from "../types/expense";
import {
    getExpenses,
    createExpense,
    updateExpense,
    deleteExpense
} from "../api/expenseApi";
import { handleWithAlert } from "../utils/handleWithAlert";

interface ExpenseContextProps {
    expenses: Expense[];
    addExpense: (data: Omit<Expense, "id">) => Promise<void>;
    updateExpense: (data: Expense) => Promise<void>;
    deleteExpense: (id: string) => Promise<void>;
    fetchExpenses: () => Promise<void>;
}

const ExpenseContext = createContext<ExpenseContextProps | undefined>(undefined);

export const ExpenseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [expenses, setExpenses] = useState<Expense[]>([]);

    const fetchExpenses = async () => {
        const list = await handleWithAlert(getExpenses, { errorMessage: "Failed to fetch expenses" });
        if (list) setExpenses(list.map((r: any) => ({ ...r, id: r._id || r.id })));
    };

    const addExpense = async (data: Omit<Expense, "id">) => {
        const created = await handleWithAlert(
            () => createExpense(data),
            { successMessage: "Expense created!", errorMessage: "Failed to create", showSuccess: true }
        );
        if (created) fetchExpenses();
    };

    const updateExpenseFn = async (updated: Expense) => {
        const res = await handleWithAlert(
            () => updateExpense(updated.id!, updated),
            { successMessage: "Expense updated!", errorMessage: "Failed to update", showSuccess: true }
        );
        if (res) fetchExpenses();
    };

    const deleteExpenseFn = async (id: string) => {
        const res = await handleWithAlert(
            () => deleteExpense(id),
            { successMessage: "Expense deleted!", errorMessage: "Failed to delete", showSuccess: true }
        );
        if (res !== null) setExpenses(prev => prev.filter(r => r.id !== id));
    };

    return (
        <ExpenseContext.Provider value={{
            expenses,
            addExpense,
            updateExpense: updateExpenseFn,
            deleteExpense: deleteExpenseFn,
            fetchExpenses
        }}>
            {children}
        </ExpenseContext.Provider>
    );
};

export const useExpense = () => {
    const ctx = useContext(ExpenseContext);
    if (!ctx) throw new Error("useExpense must be inside ExpenseProvider");
    return ctx;
};
